/**
 * The condition grammar — Step 28/29/57. A recursive JSON structure, not a
 * JavaScript expression string: a string is an RCE surface and can't be
 * rendered back as a readable sentence in the builder; a grammar can be
 * validated, diffed, and shown as one. Shared with the automations engine
 * (Step 32) — the same evaluator, two callers.
 *
 * A condition may only reference a field EARLIER in the form's field order.
 * That single rule makes the dependency graph a DAG by construction and
 * kills cycle detection before it is ever needed — enforced by the builder
 * (Step 29), not by this evaluator, which just reads whatever values it's
 * handed.
 */
import { z } from 'zod';

export type Condition =
  | { op: 'eq' | 'neq'; fieldKey: string; value: string | number | boolean | null }
  | { op: 'in' | 'notIn'; fieldKey: string; value: (string | number)[] }
  // Substring match, case-insensitive — the people-filter's "job title
  // contains Manager" needs this; eq/in alone can't express it.
  | { op: 'contains' | 'notContains'; fieldKey: string; value: string }
  // Step 120 — numeric comparison, for the time facts ("hours since the
  // requester replied is more than 48"). Anything that is not a finite
  // number on either side is simply not greater/less — never a coercion
  // surprise, never a throw.
  | { op: 'gt' | 'gte' | 'lt' | 'lte'; fieldKey: string; value: number }
  | { op: 'isEmpty' | 'isNotEmpty'; fieldKey: string }
  | { op: 'and' | 'or'; conditions: Condition[] };

/** The one recursive validator for the grammar above — every server route
 *  that accepts a Condition (form-layout sections, the people-filter below,
 *  automations later) shares this rather than redefining it per call site. */
export const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    z.object({
      op: z.enum(['eq', 'neq']),
      fieldKey: z.string(),
      value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    }),
    z.object({
      op: z.enum(['in', 'notIn']),
      fieldKey: z.string(),
      value: z.array(z.union([z.string(), z.number()])),
    }),
    z.object({ op: z.enum(['contains', 'notContains']), fieldKey: z.string(), value: z.string() }),
    z.object({ op: z.enum(['gt', 'gte', 'lt', 'lte']), fieldKey: z.string(), value: z.number().finite() }),
    z.object({ op: z.enum(['isEmpty', 'isNotEmpty']), fieldKey: z.string() }),
    z.object({ op: z.enum(['and', 'or']), conditions: z.array(conditionSchema) }),
  ]),
);

/** Step 120 — a number, or nothing. Strings that are numbers count ("48"
 *  from a text box); empty strings, nulls and words do not. */
function numeric(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
}

export function evaluateCondition(condition: Condition, values: Record<string, unknown>): boolean {
  switch (condition.op) {
    case 'eq':
      return values[condition.fieldKey] === condition.value;
    case 'neq':
      return values[condition.fieldKey] !== condition.value;
    case 'in':
      return condition.value.includes(values[condition.fieldKey] as string | number);
    case 'notIn':
      return !condition.value.includes(values[condition.fieldKey] as string | number);
    case 'contains':
      return String(values[condition.fieldKey] ?? '')
        .toLowerCase()
        .includes(condition.value.toLowerCase());
    case 'notContains':
      return !String(values[condition.fieldKey] ?? '')
        .toLowerCase()
        .includes(condition.value.toLowerCase());
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const left = numeric(values[condition.fieldKey]);
      if (left === null) return false;
      const right = condition.value;
      return condition.op === 'gt'
        ? left > right
        : condition.op === 'gte'
          ? left >= right
          : condition.op === 'lt'
            ? left < right
            : left <= right;
    }
    case 'isEmpty':
      return isEmpty(values[condition.fieldKey]);
    case 'isNotEmpty':
      return !isEmpty(values[condition.fieldKey]);
    case 'and':
      return condition.conditions.every((c) => evaluateCondition(c, values));
    case 'or':
      return condition.conditions.some((c) => evaluateCondition(c, values));
  }
}
